import { useEffect, useState } from "react";
import { api, apiErr } from "@/api";
import Shell from "@/components/Shell";
import { exhibitorNav } from "./ExhibitorDashboard";
import { Spinner, Overline } from "@/components/Brand";
import ProductEditor from "@/components/ProductEditor";
import PhotoIntake from "@/components/PhotoIntake";
import ProductImages from "@/components/ProductImages";
import ProductFilters, { EMPTY_FILTERS, filterProducts } from "@/components/ProductFilters";
import AddToCatalogue from "./AddToCatalogue";
import { useCredits } from "@/hooks/useCredits";
import { useTaxonomy } from "@/hooks/useTaxonomy";
import { toast } from "sonner";
import { Plus, Trash2, Boxes, Pencil, CheckSquare, Square, BookPlus } from "lucide-react";

function ProductTile({ pr, selecting, selected, onToggle, onEdit, onDelete }) {
  const mats = Array.isArray(pr.materials) ? pr.materials : [];
  return (
    <div className={`glass rounded-2xl overflow-hidden relative ${selected ? "ring-2 ring-amber-400" : ""}`} data-testid={`product-${pr.id}`}>
      {selecting && (
        <button onClick={onToggle} className="absolute inset-0 z-10" data-testid={`select-product-${pr.id}`}>
          <span className="absolute top-2 left-2 h-7 w-7 rounded-lg bg-white/90 flex items-center justify-center text-amber-600">{selected ? <CheckSquare size={16} /> : <Square size={16} />}</span>
        </button>
      )}
      {(pr.images?.length || pr.image) ? <ProductImages product={pr} rounded="rounded-none" testid={`product-img-${pr.id}`} />
        : <div className="w-full aspect-square bg-amber-500/5 flex items-center justify-center"><Boxes size={22} className="text-amber-400/50" /></div>}
      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="text-slate-100 text-sm font-medium leading-tight">{pr.name}</div>
          {!selecting && (
            <div className="flex gap-2 shrink-0">
              <button onClick={onEdit} className="text-slate-400 hover:text-amber-300" data-testid={`edit-product-${pr.id}`}><Pencil size={13} /></button>
              <button onClick={onDelete} className="text-red-300" data-testid={`del-product-${pr.id}`}><Trash2 size={13} /></button>
            </div>
          )}
        </div>
        <div className="text-[10px] text-amber-300 mt-0.5">{pr.category}{mats.length ? ` · ${mats.slice(0, 2).join(", ")}` : ""}</div>
        {pr.style_tags?.length > 0 && <div className="text-[10px] text-slate-500 mt-0.5 truncate">{pr.style_tags.join(" · ")}</div>}
      </div>
    </div>
  );
}

export default function ProductLibrary() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [f, setF] = useState(EMPTY_FILTERS);
  const [selecting, setSelecting] = useState(false);
  const [sel, setSel] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const { credits, setCredits, costs } = useCredits();
  const tax = useTaxonomy();

  const load = async () => {
    const { data } = await api.get("/exhibitor/products");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this product from your library?")) { return; }
    await api.delete(`/exhibitor/products/${id}`);
    toast.success("Removed");
    load();
  };
  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${sel.length} product(s)?`)) { return; }
    try { await api.post("/exhibitor/products/bulk-delete", { ids: sel }); toast.success("Deleted"); setSel([]); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const shown = filterProducts(items, f);

  return (
    <Shell nav={exhibitorNav}>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <Overline>My Products</Overline>
          <h1 className="font-display text-3xl gold-text mt-2">Product Library</h1>
          <p className="text-slate-400 text-sm mt-1">Your reusable jewellery, across every JJS edition. <span className="text-amber-300" data-testid="library-credits">{credits} credits</span></p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setSelecting(!selecting); setSel([]); }} className="glass px-4 py-2.5 rounded-xl text-sm text-slate-200 border border-amber-500/20" data-testid="library-select-toggle">{selecting ? "Cancel" : "Select"}</button>
          <button onClick={() => setIntakeOpen(true)} className="btn-gold px-4 py-2.5 rounded-xl flex items-center gap-2" data-testid="add-product-button"><Plus size={16} /> Add products</button>
        </div>
      </div>

      <div className="mt-5"><ProductFilters f={f} setF={setF} tax={tax} /></div>

      {!items ? <Spinner /> : items.length === 0 ? (
        <div className="glass rounded-2xl p-10 text-center mt-6" data-testid="products-empty">
          <Boxes size={32} className="text-amber-400 mx-auto" />
          <p className="text-slate-400 text-sm mt-3">No products yet. Add your first jewellery photos.</p>
        </div>
      ) : (
        <>
          <p className="text-[11px] text-slate-500 mt-4" data-testid="library-count">{shown.length} of {items.length} products</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-2 pb-24">
            {shown.map((pr) => <ProductTile key={pr.id} pr={pr} selecting={selecting} selected={sel.includes(pr.id)} onToggle={() => toggle(pr.id)} onEdit={() => setEditing(pr)} onDelete={() => del(pr.id)} />)}
          </div>
        </>
      )}

      {selecting && sel.length > 0 && (
        <div className="fixed bottom-20 md:bottom-6 inset-x-0 z-40 flex justify-center px-4" data-testid="bulk-bar">
          <div className="glass rounded-2xl shadow-xl px-4 py-3 flex items-center gap-3 border border-amber-500/30">
            <span className="text-sm text-slate-200" data-testid="bulk-count">{sel.length} selected</span>
            <button onClick={() => setAddOpen(true)} className="btn-gold px-3 py-2 rounded-xl text-sm flex items-center gap-1.5" data-testid="bulk-add-to-catalogue"><BookPlus size={14} /> Add to Catalogue</button>
            <button onClick={bulkDelete} className="text-red-400 text-sm flex items-center gap-1" data-testid="bulk-delete"><Trash2 size={14} /> Delete</button>
          </div>
        </div>
      )}

      {editing && <ProductEditor product={editing} credits={credits} setCredits={setCredits} costs={costs} onClose={() => { setEditing(null); load(); }} onSaved={() => { setEditing(null); load(); }} />}
      {intakeOpen && <PhotoIntake credits={credits} setCredits={setCredits} costs={costs} onApproved={() => load()} onClose={() => { setIntakeOpen(false); load(); }} />}
      {addOpen && <AddToCatalogue productIds={sel} credits={credits} setCredits={setCredits} costs={costs} onClose={() => setAddOpen(false)} onDone={() => { setAddOpen(false); setSelecting(false); setSel([]); }} />}
    </Shell>
  );
}
